import React, { useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  useColorModeValue,
  Spinner,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
} from "@chakra-ui/react";
import { HSeparator } from "components/separator/Separator";
import DefaultAuth from "layouts/auth/Default";
import illustration from "assets/img/auth/auth.png";
import { FcGoogle } from "react-icons/fc";
import { MdOutlineRemoveRedEye } from "react-icons/md";
import { RiEyeCloseLine } from "react-icons/ri";
import { useTranslation } from 'react-i18next';
import constants from '../../../providers/Constants';
import useAuthentication from "../../../providers/Authentication";
import { BlockMapDevelopersLogo, BlockMapWhiteDevelopersLogo } from "components/icons/Icons";

function SignUp() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const email = params.get('email') || '';
  const { t, i18n } = useTranslation();
  const textColor = useColorModeValue("navy.700", "white");
  const textColorSecondary = "gray.400";
  const textColorDetails = useColorModeValue("navy.700", "secondaryGray.600");
  const textColorBrand = useColorModeValue("brand.500", "white");
  const brandStars = useColorModeValue("brand.500", "brand.400");
  const googleBg = useColorModeValue("secondaryGray.300", "whiteAlpha.200");
  const googleText = useColorModeValue("navy.700", "white");
  const googleHover = useColorModeValue(
    { bg: "gray.200" },
    { bg: "whiteAlpha.300" }
  );
  const googleActive = useColorModeValue(
    { bg: "secondaryGray.300" },
    { bg: "whiteAlpha.200" }
  );
  const shadow = useColorModeValue(
    '14px 17px 40px 4px rgba(112, 144, 176, 0.18)',
    '14px 17px 40px 4px rgba(112, 144, 176, 0.06)',
  );
  const menuBg = useColorModeValue('white', 'navy.800');
  const countryHoverBg = useColorModeValue('gray.100', 'whiteAlpha.100');
  const [show, setShow] = React.useState(false);
  const [postData, setPostData] = React.useState({});
  const [error, setError] = React.useState([]);
  const [countries, setCountries] = React.useState([]);
  const [filteredCountries, setFilteredCountries] = React.useState([]);
  const [countrySearch, setCountrySearch] = React.useState('');
  const [countryInputFocused, setCountryInputFocused] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [loader, setLoader] = React.useState(false);

  const authentication = useAuthentication();
  const handleClick = () => setShow(!show);

  //   Chakra color mode
  let logoColor = useColorModeValue("navy.700", "white");

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        await constants.clearData();
        constants.switchEnvironment('PRODUCTION', false);
      } catch (e) { }
      setPostData((prevData) => ({
        ...prevData,
        email: email,
      }));
      getCountries();
    }
    fetchData();
  }, []);

  const handleCountrySearch = (e) => {
    const searchValue = e.target.value;
    setCountrySearch(searchValue);

    if (searchValue.trim() === '') {
      setFilteredCountries(countries);
    } else {
      const filtered = countries.filter((country) =>
        country.country_name.toLowerCase().includes(searchValue.toLowerCase()) ||
        country.country_iso_code_2.toLowerCase().includes(searchValue.toLowerCase())
      );
      setFilteredCountries(filtered);
    }
  };

  const handleSelectCountry = (country) => {
    setPostData({ ...postData, country_shortcode: country.country_iso_code_2 });
    setCountrySearch(country.country_name);
    setCountryInputFocused(false);
    setFilteredCountries([]);
  };

  const handleCountryInputFocus = () => {
    setCountryInputFocused(true);
    setFilteredCountries(countries);
  };

  const handleCountryInputBlur = () => {
    setCountryInputFocused(false);
    setTimeout(() => {
      setFilteredCountries([]);
    }, 200);
  };

  return (
    <DefaultAuth illustrationBackground={illustration} image={illustration}>
      {/* Language Switcher - fixed at top right */}
      <Flex position='fixed' top='16px' right='24px' zIndex={1500} gap='12px'>
        {/* Language Switcher */}
        <Menu>
          <MenuButton as={Button} variant="ghost" color={textColorSecondary} px="8px">
            <Text fontSize="sm" fontWeight="600" color={textColor} textTransform="uppercase">
              {i18n.language === 'fr' ? 'FR' : 'EN'}
            </Text>
          </MenuButton>
          <MenuList boxShadow={shadow} p="10px" borderRadius="12px" bg={menuBg} border="none">
            <MenuItem onClick={() => { i18n.changeLanguage('en'); if (typeof window !== 'undefined') window.localStorage.setItem('app.lang', 'en'); }}>
              {t('English')} (EN)
            </MenuItem>
            <MenuItem onClick={() => { i18n.changeLanguage('fr'); if (typeof window !== 'undefined') window.localStorage.setItem('app.lang', 'fr'); }}>
              {t('French')} (FR)
            </MenuItem>
          </MenuList>
        </Menu>
      </Flex>
      {loading ? (
        <Flex h="100vh" alignItems="center" justifyContent="flex-start" pl="28vh">
          <Spinner size="xl" />
        </Flex>
      ) : (
        <Flex
          maxW={{ base: "100%", md: "max-content" }}
          w='100%'
          mx={{ base: "auto", lg: "0px" }}
          me='auto'
          h='100%'
          alignItems='start'
          justifyContent='center'
          mb={{ base: "10px", md: "10px" }}
          px={{ base: "0px", md: "0px" }}
          mt={{ base: "10px", md: "10px" }}
          flexDirection='column'>
          {
            logoColor === "white" ? (
              <BlockMapWhiteDevelopersLogo h='150px' w='250px' my='0px' mx='0px' />
            ) : (
              <BlockMapDevelopersLogo h='150px' w='250px' my='0px' mx='0px' />
            )
          }
          <Box me='auto'>
            <Heading color={textColor} fontSize='36px' mb='10px'>
              {t('Sign Up')}
            </Heading>
            <Text
              mb='36px'
              ms='4px'
              color={textColorSecondary}
              fontWeight='400'
              fontSize='md'>
              {t('Enter your basic information to create an account!')}
            </Text>
          </Box>
          <Flex
            zIndex='2'
            direction='column'
            w={{ base: "100%", md: "420px" }}
            maxW='100%'
            background='transparent'
            borderRadius='15px'
            mx={{ base: "auto", lg: "unset" }}
            me='auto'
            mb={{ base: "20px", md: "auto" }}>
            <FormControl>
              <FormLabel
                display='flex'
                ms='4px'
                fontSize='sm'
                fontWeight='500'
                color={textColor}
                mb='8px'>
                {t('Country')}<Text color={brandStars}>*</Text>
              </FormLabel>
              <Box position='relative' mb='24px'>
                <Input
                  placeholder={t('Search countries...')}
                  value={countrySearch}
                  onChange={handleCountrySearch}
                  onFocus={handleCountryInputFocus}
                  onBlur={handleCountryInputBlur}
                  fontSize='sm'
                  fontWeight='500'
                  size='lg'
                  variant='auth'
                />
                <Box
                  position='absolute'
                  top='100%'
                  left='0'
                  right='0'
                  maxH='300px'
                  overflowY='auto'
                  bg={menuBg}
                  borderRadius='12px'
                  border='1px solid #E2E8F0'
                  zIndex={10}
                  mt='4px'
                  display={countryInputFocused && filteredCountries.length > 0 ? 'block' : 'none'}
                  boxShadow='0px 9px 16px rgba(112, 144, 176, 0.18)'
                >
                  {filteredCountries.length > 0 ? (
                    filteredCountries.map((country, index) => (
                      <Box
                        key={index}
                        p='12px 16px'
                        cursor='pointer'
                        _hover={{ bg: countryHoverBg }}
                        onMouseDown={() => handleSelectCountry(country)}
                        borderBottom='1px solid #E2E8F0'
                        display='flex'
                        alignItems='center'
                        gap='10px'
                      >
                        {(country.country_avatar_2 || country.country_avatar) && (
                          <Box
                            as='img'
                            src={country.country_avatar_2 || country.country_avatar}
                            alt={country.country_name}
                            w='24px'
                            h='24px'
                            borderRadius='4px'
                            objectFit='cover'
                          />
                        )}
                        <Text fontSize='sm' fontWeight='500' color={textColor}>
                          {country.country_name}
                        </Text>
                      </Box>
                    ))
                  ) : (
                    <Box p='12px 16px'>
                      <Text fontSize='sm' color={textColorSecondary}>
                        {t('No countries found')}
                      </Text>
                    </Box>
                  )}
                </Box>
                <select
                  required
                  value={postData.country_shortcode || ''}
                  onChange={(e) => {
                    setPostData({ ...postData, country_shortcode: e.target.value });
                    const selected = countries.find(c => c.country_iso_code_2 === e.target.value);
                    if (selected) setCountrySearch(selected.country_name);
                  }}
                  style={{
                    position: 'absolute',
                    opacity: '0',
                    pointerEvents: 'none',
                    width: '100%',
                    height: '0',
                  }}
                >
                  <option value="" disabled>{t('Choose Your Country')}</option>
                  {
                    countries.map((country, index) => (
                      <option key={index} value={country?.country_iso_code_2}>{country.country_name}</option>
                    ))
                  }
                </select>
              </Box>
              <FormLabel
                ms='4px'
                fontSize='sm'
                fontWeight='500'
                color={textColor}
                display='flex'>
                {t('Business Name')}<Text color={brandStars}>*</Text>
              </FormLabel>
              <InputGroup size='md'>
                <Input
                  isRequired={true}
                  variant='auth'
                  fontSize='sm'
                  ms={{ base: "0px", md: "0px" }}
                  type='text'
                  placeholder='BlockMap Inc.'
                  mb='24px'
                  fontWeight='500'
                  size='lg'
                  onChange={(e) => setPostData({ ...postData, business_name: e.target.value })}
                />
              </InputGroup>
              <Flex justifyContent="space-between">
                <Box w="48%">
                  <FormLabel
                    display='flex'
                    ms='4px'
                    fontSize='sm'
                    fontWeight='500'
                    color={textColor}
                    mb='8px'>
                    {t('First Name')}<Text color={brandStars}>*</Text>
                  </FormLabel>
                  <Input
                    isRequired={true}
                    variant='auth'
                    fontSize='sm'
                    ms={{ base: "0px", md: "0px" }}
                    type='text'
                    placeholder='John'
                    mb='24px'
                    fontWeight='500'
                    size='lg'
                    onChange={(e) => setPostData({ ...postData, first_name: e.target.value })}
                  />
                </Box>
                <Box w="48%">
                  <FormLabel
                    display='flex'
                    ms='4px'
                    fontSize='sm'
                    fontWeight='500'
                    color={textColor}
                    mb='8px'>
                    {t('Last Name')}<Text color={brandStars}>*</Text>
                  </FormLabel>
                  <Input
                    isRequired={true}
                    variant='auth'
                    fontSize='sm'
                    ms={{ base: "0px", md: "0px" }}
                    type='text'
                    placeholder='Doe'
                    mb='24px'
                    fontWeight='500'
                    size='lg'
                    onChange={(e) => setPostData({ ...postData, last_name: e.target.value })}
                  />
                </Box>
              </Flex>
              <Flex justifyContent="space-between">
                <Box w="48%">
                  <FormLabel
                    display='flex'
                    ms='4px'
                    fontSize='sm'
                    fontWeight='500'
                    color={textColor}
                    mb='8px'>
                    {t('Email')}<Text color={brandStars}>*</Text>
                  </FormLabel>
                  <Input
                    isRequired={true}
                    variant='auth'
                    fontSize='sm'
                    ms={{ base: "0px", md: "0px" }}
                    type='email'
                    placeholder='mail@blockmap.dev'
                    mb='24px'
                    value={postData?.email}
                    fontWeight='500'
                    size='lg'
                    onChange={(e) => setPostData({ ...postData, email: e.target.value })}
                  />
                </Box>
                <Box w="48%">
                  <FormLabel
                    display='flex'
                    ms='4px'
                    fontSize='sm'
                    fontWeight='500'
                    color={textColor}
                    mb='8px'>
                    {t('Phone')}<Text color={brandStars}>*</Text>
                  </FormLabel>
                  <Input
                    isRequired={true}
                    variant='auth'
                    fontSize='sm'
                    ms={{ base: "0px", md: "0px" }}
                    type='number'
                    placeholder='01XXXXXXXXX'
                    mb='24px'
                    fontWeight='500'
                    size='lg'
                    onChange={(e) => setPostData({ ...postData, phone: e.target.value })}
                  />
                </Box>
              </Flex>
              <FormLabel
                ms='4px'
                fontSize='sm'
                fontWeight='500'
                color={textColor}
                display='flex'>
                {t('Password')}<Text color={brandStars}>*</Text>
              </FormLabel>
              <InputGroup size='md'>
                <Input
                  isRequired={true}
                  fontSize='sm'
                  placeholder={t('Min. 12 characters')}
                  mb='24px'
                  size='lg'
                  type={show ? "text" : "password"}
                  variant='auth'
                  onChange={(e) => setPostData({ ...postData, password: e.target.value })}
                />
                <InputRightElement display='flex' alignItems='center' mt='4px'>
                  <Icon
                    color={textColorSecondary}
                    _hover={{ cursor: "pointer" }}
                    as={show ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                    onClick={handleClick}
                  />
                </InputRightElement>
              </InputGroup>
              <Flex justifyContent='space-between' align='center' mb='24px'>
                <FormControl display='flex' alignItems='center'>
                  <Checkbox
                    id='remember-login'
                    colorScheme='brandScheme'
                    me='10px'
                  />
                  <FormLabel
                    htmlFor='remember-login'
                    mb='0'
                    fontWeight='normal'
                    color={textColor}
                    fontSize='sm'>
                    {t('Keep me logged in')}
                  </FormLabel>
                </FormControl>
              </Flex>
              <Box>
                <Button
                  fontSize='sm'
                  variant='brand'
                  fontWeight='500'
                  w='100%'
                  h='50'
                  mb='24px'
                  onClick={registerUser}
                  isLoading={loader}
                  spinner={<Spinner size="sm" />}
                >
                  {t('Proceed')}
                </Button>
              </Box>
              {
                error.length > 0 && (
                  error.map((err, index) => (
                    <Text
                      key={index}
                      mb='10px'
                      ms='4px'
                      color={'red'}
                      fontWeight='400'
                      fontSize='md'>
                      * {err.msg}
                    </Text>
                  ))
                )
              }
            </FormControl>
            <Flex align='center' mb='25px'>
              <HSeparator />
              <Text color='gray.400' mx='14px'>
                {t('or')}
              </Text>
              <HSeparator />
            </Flex>
            <Flex
              flexDirection='column'
              justifyContent='center'
              alignItems='start'
              maxW='100%'
              mt='0px'>
              <Text color={textColorDetails} fontWeight='400' fontSize='14px'>
                {t('Already have an account?')}
                <NavLink to='/auth/sign-in'>
                  <Text
                    color={textColorBrand}
                    as='span'
                    ms='5px'
                    fontWeight='500'>
                    {t('Login')}
                  </Text>
                </NavLink>
              </Text>
            </Flex>
          </Flex>
        </Flex>
      )}
    </DefaultAuth>
  );

  async function getCountries() {
    setLoading(true);
    authentication.getCountries(null, (error, data) => {
      setLoading(false);
      if (error) {
        setError(error.errors);
        return constants.showToast('error', 'FAILED', error.message);
      }
      setCountries(data);
      setFilteredCountries(data);
    });
  }

  async function registerUser() {
    setLoader(true);
    authentication.register(postData, (error, data) => {
      setLoader(false);
      if (error) {
        setError(error.errors);
        return constants.showToast('error', 'FAILED', error?.message || 'Snap! Something went wrong, Try again');
      }
      constants.storeData('API_KEY', data?.developer?.api_key);
      constants.storeData('EMAIL', data?.user?.email);
      constants.storeData('AUTH_TOKEN', data?.user?.basic_token);
      constants.showToast('success', 'SUCCESS', 'Login successful');
      navigate('/admin');
    });
  }
}

export default SignUp;
